<template>
  <Modal
    v-bind="{ parentRouteName, small: true }"
    :loading="!appsLoaded"
    :saving="savingKey || savingLocations"
    @submit="submit"
  >
    <Flex
      direction="column"
      :gap="2"
    >
      <ApiKey
        v-model="apiToken"
        v-bind="{ changingKey, connected, keyLocked, saveFailed }"
        @changeKey="changeKey"
        @keepKey="keepKey"
      />
      <Locations
        v-if="showLocations"
        v-model="locationMappings"
        :options="giftyLocationOptions"
        :salonLocations="salonLocations"
        :saveFailed="saveLocationsFailed"
        :disabled="!keyLocked"
      />
    </Flex>
  </Modal>
</template>

<script
  setup
  lang="ts"
>
import { computed, inject, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { flash, modal } from '@/helpers/ui';
import type { App } from '../../types';
import Modal from '../shared/Modal.vue';
import ApiKey from './ApiKey.vue';
import Locations from './Locations.vue';
import { useApiKey } from './use-api-key';
import { useLocations } from './use-locations';

const props = defineProps<{
  parentRouteName: string;
}>();

const emit = defineEmits(['updated']);

const { t } = useI18n();
const router = useRouter();

const apps = inject<Ref<App[]>>('apps');
const appsLoaded = inject<Ref<boolean>>('appsLoaded', ref(true));
const enabled = computed(
  () => !!apps?.value.find((app) => app.key === 'gifty')?.enabled,
);

const {
  apiToken,
  changingKey,
  connected,
  keyLocked,
  saveFailed,
  savingKey,
  saveKey,
  changeKey,
  keepKey,
} = useApiKey(enabled);

const {
  giftyLocationOptions,
  locationMappings,
  salonLocations,
  saveLocationsFailed,
  savingLocations,
  showLocations,
  refetchLocations,
  saveLocations,
} = useLocations(connected);

const close = () => {
  router.push({ name: props.parentRouteName });
};

const submitLocations = () => {
  if (!showLocations.value) {
    close();
    return;
  }

  saveLocations().then((saved) => {
    if (!saved) return;

    flash(t('global.flash.integration_updated'));
    close();
  });
};

const saveNewKey = () => {
  const wasConnected = connected.value;

  saveKey().then((saved) => {
    if (!saved) return;

    emit('updated');
    flash(
      t(
        wasConnected
          ? 'global.flash.integration_updated'
          : 'global.flash.integration_enabled',
      ),
    );

    refetchLocations()
      .then((needsLocations) => {
        if (!needsLocations) close();
      })
      .catch(close);
  });
};

const submitKey = () => {
  if (!apiToken.value) return;

  if (!connected.value) {
    saveNewKey();
    return;
  }

  modal('confirmation', {
    message: t('integrations.gifty.settings.replace_key_confirmation.heading'),
    subMessage: t(
      'integrations.gifty.settings.replace_key_confirmation.description',
    ),
  }).then(saveNewKey);
};

const submit = () => {
  if (keyLocked.value) {
    submitLocations();
  } else {
    submitKey();
  }
};
</script>
